import type { FlightViewState, SortKey } from "./flightOptionsView";

interface FlightOptionsToolbarProps {
  state: FlightViewState;
  airlines: string[];
  /**
   * § G30 — departure airports actually in play, e.g. ["EWR","JFK","LGA"].
   * Rendered only when there is more than one: a single-airport trip does not
   * need a filter that can only ever mean "all", and the chip would read as a
   * choice the traveller does not have.
   */
  origins?: string[];
  /** § G31 — a return leg's chips are ARRIVAL airports, so the label says "To". */
  returnLeg?: boolean;
  shown: number;
  total: number;
  onChange: (next: FlightViewState) => void;
}

const SORTS: { key: SortKey; label: string }[] = [
  { key: "date", label: "Date" },
  { key: "duration", label: "Duration" },
  { key: "price", label: "Price" },
  { key: "stops", label: "Stops" },
  { key: "departure", label: "Departure" },
];

export default function FlightOptionsToolbar({ state, airlines, origins, returnLeg, shown, total, onChange }: FlightOptionsToolbarProps) {
  const f = state.filters;
  const toggleAirline = (code: string) => {
    const has = f.airlines.includes(code);
    const next = has ? f.airlines.filter((c) => c !== code) : [...f.airlines, code];
    onChange({ ...state, filters: { ...f, airlines: next } });
  };
  const toggleOrigin = (code: string) => {
    const has = (f.origins ?? []).includes(code);
    const next = has ? (f.origins ?? []).filter((c) => c !== code) : [...(f.origins ?? []), code];
    onChange({ ...state, filters: { ...f, origins: next } });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
      {/*
        § G30 — the airport filter leads, ahead of sort and airline.
        Somebody shopping three New York airports asks "which of these leave
        from LaGuardia?" BEFORE they care how the list is sorted: for a
        Manhattan traveller an hour to Newark can outweigh $40 of fare, and that
        is a commute decision the fare column cannot make for them.
      */}
      {(origins?.length ?? 0) > 1 && (
        <span className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-muted-foreground">{returnLeg ? 'To' : 'From'}</span>
          {origins!.map((code) => {
            const on = (f.origins ?? []).includes(code);
            return (
              <button
                key={code}
                type="button"
                aria-pressed={on}
                onClick={() => toggleOrigin(code)}
                className={
                  on
                    ? 'rounded-full border border-foreground bg-foreground px-2 py-0.5 font-mono text-[11px] text-background'
                    : 'rounded-full border border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-muted'
                }
              >
                {code}
              </button>
            );
          })}
        </span>
      )}

      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        Sort
        <select
          className="rounded border bg-background px-1.5 py-1 text-xs text-foreground"
          value={state.sort}
          onChange={(e) => onChange({ ...state, sort: e.target.value as SortKey })}
        >
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </label>

      <select
        className="rounded border bg-background px-1.5 py-1 text-xs text-foreground"
        value={f.maxStops == null ? "" : String(f.maxStops)}
        onChange={(e) =>
          onChange({ ...state, filters: { ...f, maxStops: e.target.value === "" ? null : Number(e.target.value) } })
        }
        aria-label="Max stops"
      >
        <option value="">Any stops</option>
        <option value="0">Nonstop</option>
        <option value="1">1 stop or fewer</option>
      </select>

      <select
        className="rounded border bg-background px-1.5 py-1 text-xs text-foreground"
        value={f.maxPrice == null ? "" : String(f.maxPrice)}
        onChange={(e) =>
          onChange({ ...state, filters: { ...f, maxPrice: e.target.value === "" ? null : Number(e.target.value) } })
        }
        aria-label="Max price"
      >
        <option value="">Any price</option>
        <option value="300">≤ $300</option>
        <option value="500">≤ $500</option>
        <option value="750">≤ $750</option>
        <option value="1000">≤ $1,000</option>
      </select>

      <select
        className="rounded border bg-background px-1.5 py-1 text-xs text-foreground"
        value={f.maxDurationMinutes == null ? "" : String(f.maxDurationMinutes)}
        onChange={(e) =>
          onChange({ ...state, filters: { ...f, maxDurationMinutes: e.target.value === "" ? null : Number(e.target.value) } })
        }
        aria-label="Max duration"
      >
        <option value="">Any duration</option>
        <option value="240">≤ 4h</option>
        <option value="360">≤ 6h</option>
        <option value="480">≤ 8h</option>
        <option value="720">≤ 12h</option>
      </select>

      <div role="group" aria-label="Filter by airline" className="flex flex-wrap items-center gap-1">
        {airlines.map((code) => {
          const active = f.airlines.includes(code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => toggleAirline(code)}
              aria-pressed={active}
              className={
                "rounded border px-1.5 py-0.5 text-xs tabular-nums transition-colors " +
                (active
                  ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.1] text-[hsl(var(--accent-foreground))]"
                  : "text-muted-foreground hover:bg-muted")
              }
            >
              {code}
            </button>
          );
        })}
      </div>

      <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
        {shown} of {total} shown
      </span>
    </div>
  );
}
